import { isFunction } from 'formik';
import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import RoundedDangerButton from '../../../controls/RoundedDangerButton';
import RoundedDropdownButton from '../../../controls/RoundedDropdownButton';
import RoundedGoBackButton from '../../../controls/RoundedGoBackButton';
import RoundedLinkButton from '../../../controls/RoundedLinkButton';
import RoundedPrimaryButton from '../../../controls/RoundedPrimaryButton';
import { useIsOnline } from '../../../utils/InternetConnectionProvider';
import Spinner from '../../../utils/Spinner';
import useTranslation from '../../../utils/Translations/useTranslation';
import useEntityFormContext from './useEntityFormContext';
import styles from './SubmitSection.scss';
function SubmitSection({ isDirty, isEditing = false, isNew, isSubmitting = false, onDelete, onGoBack, onCancel, createLabel, updateLabel, actions, isSubmitDisabled, goBackLabel, cancelLabel = 'Cancel', deleteLabel = 'Delete', isSubmitTogglesDropdown, isCancelDisabled = false, hasClearFormButton = false, defaultEntity = {}, leftBottomSection, pullLeft = false, submitContainerClass, submitClass, submitBtnAdditionClasses, elementRef, }) {
    const { t, direction, isLayoutRTL } = useTranslation();
    const rtlMarginClass = isLayoutRTL ? 'ml-5' : 'mr-5';
    const leftBottomSectionMarginClass = isLayoutRTL ? 'ml-10' : 'mr-10';
    const leftBottomSectionPullClass = isLayoutRTL ? 'pull-right' : 'pull-left';
    const formikBag = useEntityFormContext();
    const isClearFormButtonEnabled = useMemo(() => isFunction(hasClearFormButton)
        ? hasClearFormButton(formikBag)
        : hasClearFormButton, [formikBag, hasClearFormButton]);
    const handleResetForm = useCallback(() => {
        const { setValues } = formikBag;
        setValues(defaultEntity);
    }, [formikBag, defaultEntity]);
    const { isOnline } = useIsOnline();
    const _isSubmitDisabled = useMemo(() => {
        if (!isOnline) {
            return true;
        }
        const basicIsDisabled = isSubmitting || (!isNew && !isDirty);
        return isSubmitDisabled
            ? isSubmitDisabled(isSubmitting, { ...formikBag, dirty: isDirty })
            : basicIsDisabled;
    }, [isNew, isOnline, isSubmitting, formikBag, isSubmitDisabled, isDirty]);
    if (!onGoBack && !onCancel && !isEditing && !onDelete)
        return null;
    // eslint-disable-next-line no-nested-ternary
    const label = isSubmitting ? <Spinner /> : isNew ? createLabel : updateLabel;
    const isActionsInArray = actions instanceof Array ? !!actions.length : false;
    return (<div className={classNames(submitContainerClass, 'row')}>
      <div ref={elementRef} className={classNames(submitClass, 'action-section mt-10 col-xs-12', {
        [styles.rtlActionSection]: isLayoutRTL,
    })} data-layout-direction={direction} dir={direction}>
        {pullLeft || (leftBottomSection && !submitClass) ? (<div className={leftBottomSectionPullClass}>{leftBottomSection}</div>) : null}

        {onCancel && (<RoundedLinkButton additionClasses={classNames(submitBtnAdditionClasses, rtlMarginClass)} dataCy="cancel-button" disabled={isCancelDisabled} id="button-cancel" margin="" onClick={onCancel}>
            {t(cancelLabel)}
          </RoundedLinkButton>)}

        {isEditing && isClearFormButtonEnabled && (<RoundedLinkButton additionClasses={rtlMarginClass} dataCy="clear-button" disabled={_isSubmitDisabled} id="button-clear" margin="" onClick={handleResetForm}>
            {t('Clear')}
          </RoundedLinkButton>)}

        {onGoBack && (<RoundedGoBackButton additionClasses={classNames('btn btn-flat legitRipple', submitBtnAdditionClasses, {
        [styles.rtlGoBackSpacing]: isLayoutRTL,
    })} dataCy="go-back-button" id="button-goback" onClick={onGoBack}>
            {t(goBackLabel)}
          </RoundedGoBackButton>)}

        {!isNew && isEditing && onDelete && (<RoundedDangerButton additionClasses={rtlMarginClass} dataCy="delete-button" id="button-delete" margin="" onClick={onDelete}>
            {t(deleteLabel)}
          </RoundedDangerButton>)}

        {!pullLeft && leftBottomSection && !!submitClass ? (<div className={classNames(leftBottomSectionMarginClass, styles.leftBottomSectionInline)}>
            {leftBottomSection}
          </div>) : null}

        {isEditing &&
        (actions && isActionsInArray ? (<RoundedDropdownButton actions={actions} additionClasses={submitBtnAdditionClasses} dataCy="submit-dropdown-button" disabled={_isSubmitDisabled} isSubmitTogglesDropdown={isSubmitTogglesDropdown}>
              {label}
            </RoundedDropdownButton>) : (<RoundedPrimaryButton additionClasses="btn btn-primary legitRipple" dataCy="submit-button" disabled={isSubmitDisabled
            ? _isSubmitDisabled
            : _isSubmitDisabled || !isDirty} id="button-submit" type="submit">
              {label}
            </RoundedPrimaryButton>))}
      </div>
    </div>);
}
export default SubmitSection;
